import React, { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Modal, Button } from "react-bootstrap";
import { Printer } from "react-feather";
import VoucherPrintPreview from "./VoucherPrintPreview";
import { useLazyGetGeneralManagerSignatureQuery } from "../../../masters/employees/employeesApi";
import { blobToDataUrl } from "../../../stock/lpo/buildLpoPrintProps";

/**
 * Preview + print for a petty cash voucher.
 *
 * Available at EVERY status, deliberately: a submitted or approved voucher is exactly
 * the sheet someone carries to collect a signature or the cash, so gating print on PAID
 * would remove its main use. The status is stamped on the printout instead, so an unpaid
 * voucher can never pass as proof of payment.
 *
 * Print reuses this project's established popup pattern (qrImageUtils.printComposedImage,
 * StockDocumentPrintModal): open a blank window, write static markup rendered from the
 * SAME preview component, then print. One template for screen and paper, and no PDF
 * dependency.
 */
const VoucherPrintModal = ({ show, onClose, voucher }) => {
  // The General Manager's seal for the "Approved by" line (2026-09-09).
  // Converted to a data: URI because the print popup is an about:blank window
  // with no auth header and no base URL - a network src would render on screen
  // but break in the printed copy. Same approach as LPO and the quotation.
  // Failure is silent: the voucher prints with a blank line, as it always did.
  const [fetchGeneralManagerSignature] = useLazyGetGeneralManagerSignatureQuery();
  const [signatureImageUrl, setSignatureImageUrl] = useState(null);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    fetchGeneralManagerSignature()
      .unwrap()
      .then(blobToDataUrl)
      .then((url) => { if (!cancelled) setSignatureImageUrl(url); })
      .catch(() => { if (!cancelled) setSignatureImageUrl(null); });
    return () => { cancelled = true; };
  }, [show, fetchGeneralManagerSignature]);

  const handlePrint = () => {
    const bodyHtml = ReactDOMServer.renderToStaticMarkup(<VoucherPrintPreview voucher={voucher} signatureImageUrl={signatureImageUrl} />);

    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) return;
    win.document.write(
      `<html><head><title>Petty Cash Voucher - ${voucher?.voucherNumber || ""}</title>` +
        // A5-height content on A4 paper, same @page convention as the stock documents.
        `<style>@page { size: A4; margin: 10mm; } html,body { margin:0; padding:0; } body { width: 190mm; }</style>` +
        `</head>` +
        `<body onload="window.print();window.close();">${bodyHtml}</body></html>`
    );
    win.document.close();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          Voucher {voucher?.voucherNumber}
          <span className="text-muted ms-2 fs-6">preview</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#e9ecef" }}>
        <div style={{ background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,.2)" }}>
          <VoucherPrintPreview voucher={voucher} signatureImageUrl={signatureImageUrl} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handlePrint} className="d-inline-flex align-items-center gap-2">
          <Printer size={16} /> Print
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VoucherPrintModal;
